/**
 * Domain enumerations.
 *
 * SQLite has no native enum type, so these values live as `TEXT` in D1 and are
 * enforced by CHECK constraints in the migrations plus Zod at the API edge.
 * This file is the single definition both of those derive from — if a value is
 * not here, it is not a legal value anywhere in the system.
 */

// ---------------------------------------------------------------------------
// Timeline — dev brief §6.1
// ---------------------------------------------------------------------------

/**
 * Every write that concerns a tree produces one of these. The list is closed:
 * adding a new kind of thing that happens to a tree means adding it here and
 * to the CHECK constraint, deliberately, in a migration.
 */
export const TREE_EVENT_TYPES = [
  'PLANTED',
  'TRANSPLANTED',
  'FIRST_SPADIX',
  'FIRST_FRUIT_SET',
  'FIRST_HARVEST',
  'HARVEST',
  'MEASUREMENT',
  'HEALTH_OBSERVATION',
  'TREATMENT',
  'FERTILIZED',
  'PRUNED',
  'IRRIGATED',
  'WEEDED',
  'DAMAGED',
  'ISSUE_RAISED',
  'ISSUE_RESOLVED',
  'WORK_ORDER_COMPLETED',
  'PHOTO',
  'NOTE',
  'DEAD',
  'REMOVED',
  'REPLACED',
  'CORRECTION',
] as const;
export type TreeEventType = (typeof TREE_EVENT_TYPES)[number];

/** Drives the timeline filter chips: All · Work · Health · Harvest · … */
export const EVENT_CATEGORIES = [
  'work',
  'health',
  'harvest',
  'growth',
  'input',
  'admin',
  'note',
] as const;
export type EventCategory = (typeof EVENT_CATEGORIES)[number];

/**
 * The category each event type files under. `CORRECTION` is absent by design —
 * it inherits the category of the event it supersedes (§6.4), so a correction
 * to a harvest stays visible under the Harvest filter.
 */
export const EVENT_TYPE_CATEGORY: Record<Exclude<TreeEventType, 'CORRECTION'>, EventCategory> = {
  PLANTED: 'admin',
  TRANSPLANTED: 'admin',
  FIRST_SPADIX: 'growth',
  FIRST_FRUIT_SET: 'growth',
  FIRST_HARVEST: 'harvest',
  HARVEST: 'harvest',
  MEASUREMENT: 'growth',
  HEALTH_OBSERVATION: 'health',
  TREATMENT: 'input',
  FERTILIZED: 'input',
  PRUNED: 'work',
  IRRIGATED: 'work',
  WEEDED: 'work',
  DAMAGED: 'health',
  ISSUE_RAISED: 'health',
  ISSUE_RESOLVED: 'health',
  WORK_ORDER_COMPLETED: 'work',
  PHOTO: 'note',
  NOTE: 'note',
  DEAD: 'admin',
  REMOVED: 'admin',
  REPLACED: 'admin',
};

// ---------------------------------------------------------------------------
// Work orders — dev brief §7
// ---------------------------------------------------------------------------

export const WORK_ORDER_STATUSES = [
  'draft',
  'open',
  'assigned',
  'in_progress',
  'completed',
  'verified',
  'cancelled',
  'on_hold',
] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

/**
 * Legal transitions (§7.2). Encoded as data so the API, the UI's segmented
 * status control and the tests all read the same lifecycle.
 */
export const WORK_ORDER_TRANSITIONS: Record<WorkOrderStatus, readonly WorkOrderStatus[]> = {
  draft: ['open', 'cancelled'],
  open: ['assigned', 'in_progress', 'completed', 'on_hold', 'cancelled'],
  assigned: ['in_progress', 'completed', 'on_hold', 'cancelled'],
  in_progress: ['completed', 'on_hold', 'cancelled'],
  completed: ['verified', 'in_progress'], // reopening returns it to in_progress
  verified: [],
  cancelled: [],
  on_hold: ['open', 'assigned', 'in_progress', 'cancelled'],
};

export function canTransition(from: WorkOrderStatus, to: WorkOrderStatus): boolean {
  return WORK_ORDER_TRANSITIONS[from].includes(to);
}

/** Statuses that count as "the work is finished" for reporting and overdue. */
export const WORK_ORDER_CLOSED_STATUSES: readonly WorkOrderStatus[] = [
  'completed',
  'verified',
  'cancelled',
];

export const WORK_ORDER_LINE_STATUSES = ['pending', 'done', 'skipped', 'blocked'] as const;
export type WorkOrderLineStatus = (typeof WORK_ORDER_LINE_STATUSES)[number];

/** A line no longer needing action — the set that auto-completes an order. */
export const WORK_ORDER_LINE_RESOLVED: readonly WorkOrderLineStatus[] = ['done', 'skipped'];

export const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const WORK_ORDER_EVENT_TYPES = [
  'CREATED',
  'OPENED',
  'ASSIGNED',
  'PRINTED',
  'STARTED',
  'LINE_COMPLETED',
  'LINES_BULK_UPDATED',
  'COMPLETED',
  'VERIFIED',
  'REOPENED',
  'CANCELLED',
  'EDITED',
  'LINE_ADDED',
  'LINE_REMOVED',
] as const;
export type WorkOrderEventType = (typeof WORK_ORDER_EVENT_TYPES)[number];

export const TASK_TYPE_CODES = [
  'FERTILIZE',
  'CROWN_CLEAN',
  'PEST_TREAT',
  'HARVEST',
  'INSPECT',
  'IRRIGATE',
  'WEED',
  'PRUNE',
  'REPLANT',
  'TAG_REPAIR',
] as const;
export type TaskTypeCode = (typeof TASK_TYPE_CODES)[number];

// ---------------------------------------------------------------------------
// Trees and positions
// ---------------------------------------------------------------------------

export const TREE_STATUSES = ['alive', 'dead', 'removed', 'replaced'] as const;
export type TreeStatus = (typeof TREE_STATUSES)[number];

export const REMOVAL_REASONS = ['disease', 'storm', 'low_yield', 'age', 'other'] as const;
export type RemovalReason = (typeof REMOVAL_REASONS)[number];

export const POSITION_STATUSES = ['active', 'vacant', 'retired', 'reserved'] as const;
export type PositionStatus = (typeof POSITION_STATUSES)[number];

export const TAG_CONDITIONS = ['good', 'faded', 'damaged', 'missing'] as const;
export type TagCondition = (typeof TAG_CONDITIONS)[number];

export const VARIETY_TYPES = ['tall', 'dwarf', 'hybrid'] as const;
export type VarietyType = (typeof VARIETY_TYPES)[number];

export const PLANTING_PATTERNS = [
  'square',
  'triangular',
  'single_hedge',
  'double_hedge',
] as const;
export type PlantingPattern = (typeof PLANTING_PATTERNS)[number];

/**
 * How much to trust a date — dev brief §16 item 3 and §17.5.
 *
 * Most of the 625 trees have no recorded planting date, so their ages will be
 * estimated at import. Without this flag those estimates become facts within a
 * year and every age-vs-yield comparison built on them is quietly wrong.
 */
export const DATA_CONFIDENCE = ['recorded', 'estimated', 'unknown'] as const;
export type DataConfidence = (typeof DATA_CONFIDENCE)[number];

// ---------------------------------------------------------------------------
// Records
// ---------------------------------------------------------------------------

export const HARVEST_GRADES = ['mature', 'tender', 'immature', 'reject'] as const;
export type HarvestGrade = (typeof HARVEST_GRADES)[number];

export const HARVEST_DESTINATIONS = ['fresh', 'copra', 'oil', 'seednut'] as const;
export type HarvestDestination = (typeof HARVEST_DESTINATIONS)[number];

export const AFFECTED_PARTS = ['crown', 'frond', 'trunk', 'bunch', 'root'] as const;
export type AffectedPart = (typeof AFFECTED_PARTS)[number];

/** 0 = none observed, 4 = critical. Rendered as a five-block severity meter. */
export const SEVERITY_MIN = 0;
export const SEVERITY_MAX = 4;

export const PEST_DISEASE_CATEGORIES = ['pest', 'disease', 'deficiency', 'abiotic'] as const;
export type PestDiseaseCategory = (typeof PEST_DISEASE_CATEGORIES)[number];

export const PRODUCT_TYPES = [
  'fertiliser',
  'pesticide',
  'fungicide',
  'herbicide',
  'biological',
  'other',
] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const MOVEMENT_TYPES = [
  'purchase',
  'issue',
  'return',
  'adjustment',
  'wastage',
] as const;
export type MovementType = (typeof MOVEMENT_TYPES)[number];

export const ISSUE_STATUSES = ['open', 'triaged', 'scheduled', 'resolved', 'wont_fix'] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

export const PAYMENT_STATUSES = ['unpaid', 'partial', 'paid'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const ATTACHMENT_STATUSES = ['pending', 'committed', 'processed', 'failed'] as const;
export type AttachmentStatus = (typeof ATTACHMENT_STATUSES)[number];

/**
 * Polymorphic attachment targets — the only photo mechanism in the system.
 *
 * Must stay in step with the CHECK constraint on `attachment.entity_type`;
 * migration 0007 added `site` and `block` and rebuilt the table to do it.
 */
export const ATTACHABLE_ENTITIES = [
  'tree_event',
  'work_order',
  'work_order_line',
  'issue',
  /** The farm's own photograph. */
  'site',
  /** A zone's. */
  'block',
] as const;
export type AttachableEntity = (typeof ATTACHABLE_ENTITIES)[number];

export const WORKER_ROLES = ['harvester', 'general', 'sprayer', 'supervisor'] as const;
export type WorkerRole = (typeof WORKER_ROLES)[number];

export const USER_STATUSES = ['active', 'suspended', 'invited'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const LOCALES = ['lo', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/** Lao is the default everywhere — UI brief §5. English is a per-user toggle. */
export const DEFAULT_LOCALE: Locale = 'lo';
